// v5.4.0 · Panel del expediente del estudiante (vista del personal): docente acompañante, avance del curso,
// alerta de inactividad (≥ 2 días), padre/tutor para los avisos y resultado de las evaluaciones post-clase.
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, Rocket, BellRing, Save, Loader2, ClipboardCheck } from "lucide-react";

export default function StudentExtras({ studentId }) {
  const [x, setX] = useState(null);
  const [g, setG] = useState({ guardian_name: "", guardian_email: "", course_total_classes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(`/student-extras/${studentId}`).then(({ data }) => {
    setX(data);
    setG({ guardian_name: data.guardian?.name || "", guardian_email: data.guardian?.email || "",
      course_total_classes: data.course?.explicit_total ? String(data.course.classes_total) : "" });
  }).catch(() => setX(null)), [studentId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { guardian_name: g.guardian_name, guardian_email: g.guardian_email };
      body.course_total_classes = g.course_total_classes === "" ? 0 : Number(g.course_total_classes);
      const { data } = await api.put(`/student-extras/${studentId}/guardian`, body);
      setX((prev) => ({ ...prev, ...data }));
      toast.success("Datos guardados");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!x) return null;
  const inactive = x.inactive_days != null && x.inactive_days >= 2;
  const ev = x.class_evals || [];
  return (
    <div className="space-y-3" data-testid="student-extras">
      {inactive && (
        <div className="rounded-xl border border-[#F5B82E] bg-[#FFF7DA] p-3 flex items-start gap-3" data-testid="alerta-inactividad">
          <BellRing size={18} className="text-[#C98A00] mt-0.5 shrink-0" />
          <p className="text-sm text-[#1F2547]">
            <b>Sin ingresar hace {x.inactive_days} días.</b>{" "}
            {x.guardian?.last_alert_at
              ? `Último aviso al padre/tutor: ${new Date(x.guardian.last_alert_at).toLocaleDateString("es-MX", { dateStyle: "medium" })}.`
              : x.guardian?.email ? "El aviso al padre/tutor se envía automáticamente." : "Registra el correo del padre/tutor para avisarle automáticamente."}
          </p>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="premium-card p-4" data-testid="extras-docente">
          <p className="text-[11px] uppercase tracking-wider font-bold text-[#A43790] inline-flex items-center gap-1"><UserCheck size={12} /> Docente acompañante</p>
          <p className="text-base font-bold text-[#2C3539] mt-1">{x.teacher?.name || "Sin docente asignado"}</p>
          {x.teacher?.email && <p className="text-xs text-[#5C666B]">{x.teacher.email}</p>}
        </div>
        <div className="premium-card p-4" data-testid="extras-curso">
          <p className="text-[11px] uppercase tracking-wider font-bold text-[#A43790] inline-flex items-center gap-1"><Rocket size={12} /> Avance del curso</p>
          <p className="text-2xl font-bold text-[#2C3539] mt-1">{x.course.percent}%</p>
          <div className="h-2 rounded-full bg-[#E8E2D9] overflow-hidden mt-1"><div className="h-full bg-[#A43790]" style={{ width: `${x.course.percent}%` }} /></div>
          <p className="text-xs text-[#5C666B] mt-1">{x.course.classes_done} de {x.course.classes_total} clases{x.ruta ? ` · mundos ${x.ruta.mundos_completados}/${x.ruta.mundos_total}` : ""}</p>
        </div>
        <div className="premium-card p-4" data-testid="extras-evals">
          <p className="text-[11px] uppercase tracking-wider font-bold text-[#A43790] inline-flex items-center gap-1"><ClipboardCheck size={12} /> Evaluación post-clase</p>
          {ev.length === 0 ? <p className="text-sm text-[#5C666B] mt-1">Aún sin respuestas.</p> : (
            <ul className="mt-1 space-y-1">
              {ev.slice(0, 3).map((e) => (
                <li key={e.id} className="text-xs text-[#2C3539]">
                  <b>{e.class_title || "Clase"}</b> · entendió {e.answers.comprension}/5 · seguridad {e.answers.confianza}/5
                  {e.needs_attention && <span className="ml-1 text-[#D64545] font-bold">· refuerzo</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="premium-card p-4" data-testid="extras-tutor">
        <p className="text-[11px] uppercase tracking-wider font-bold text-[#A43790]">Padre / tutor (recibe el aviso si no ingresa en 2 días)</p>
        <div className="grid gap-2 md:grid-cols-4 mt-2">
          <input value={g.guardian_name} onChange={(e) => setG({ ...g, guardian_name: e.target.value })} placeholder="Nombre" data-testid="tutor-nombre"
            className="px-3 py-2 text-sm rounded-md border border-[#E8E2D9]" />
          <input type="email" value={g.guardian_email} onChange={(e) => setG({ ...g, guardian_email: e.target.value })} placeholder="Correo" data-testid="tutor-correo"
            className="px-3 py-2 text-sm rounded-md border border-[#E8E2D9]" />
          <input type="number" min={0} value={g.course_total_classes} onChange={(e) => setG({ ...g, course_total_classes: e.target.value })}
            placeholder="Clases del curso (opcional)" title="Total de clases del curso; vacío = clases tomadas + programadas" data-testid="curso-total"
            className="px-3 py-2 text-sm rounded-md border border-[#E8E2D9]" />
          <button type="button" onClick={save} disabled={saving} data-testid="tutor-guardar"
            className="inline-flex items-center justify-center gap-2 bg-[#A43790] text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
